import { useEffect, useState } from "react";
import { apiFetch } from "../utils/apiFetch";

/** Eine Firma im transitiven Firmenverbund (Konzern). */
export interface CompanyNetworkFirma {
  id: number;
  name: string | null;
  /**
   * Beziehung aus Sicht der Ausgangsfirma ('Tochterfirma' | 'Mutterfirma' |
   * 'Schwester' | 'Beteiligung'); null für die Ausgangsfirma selbst und für
   * Firmen, die nur transitiv (über Zwischenfirmen) erreichbar sind.
   */
  role?: string | null;
  /** Branche als Ebene-3-Blatt (ContactBrancheClassification) — via findLeaf auflösen. */
  brancheClassId?: number | null;
  /** Freitext „besondere/betriebsunübliche Tätigkeiten". */
  besonderheiten?: string | null;
  /**
   * Aufgabe(n) im Unternehmensverbund (Betriebs-, Besitz-,
   * Holdinggesellschaft) — mehrere, mit „; " verbunden. Bewusst nicht
   * „role": das ist hier schon die Beziehung zur Ausgangsfirma.
   */
  unternehmensfunktion?: string | null;
  /** Tätigkeit / Betriebscharakter, wie im Reiter „Allgemein" gepflegt. */
  taetigkeit?: string | null;
  /** Was der Kunde selbst im Risikoanalyse-Fragebogen beschrieben hat. */
  taetigkeitLautKunde?: string | null;
  /** Weitere Branchen neben der Hauptbranche (ContactBrancheAssignment). */
  weitereBranchen?: { brancheClassId: number }[];
  /** Hauptanschrift als eine Zeile („Straße, PLZ Ort") — Standardadresse zuerst. */
  anschrift?: string | null;
}

/**
 * Transitiver Firmenverbund (Konzern) einer Firma — alle über die Beziehungskette
 * (Tochterfirma/Schwester/Beteiligung, beide Richtungen) erreichbaren Firmen, inkl.
 * der Firma selbst. Backend: GET /contact-relation/company-network/:id.
 */
export const fetchCompanyNetwork = async (contactId: number): Promise<CompanyNetworkFirma[]> => {
  const { data } = await apiFetch<CompanyNetworkFirma[]>(
    `contact-relation/company-network/${contactId}`,
  );
  return data ?? [];
};

/** Knoten des Organigramm-Graphen: Firmen des Verbunds + strukturrelevante Personen. */
export interface OrgGraphNode {
  id: number;
  name: string | null;
  typ: "Firma" | "Person";
  isSelf: boolean;
  /**
   * Stammdaten des Kästchens — nur an Firmen-Knoten belegt. Das Organigramm im
   * Risiken-Reiter zeigt sie IM Kästchen an; ohne sie stünde dort nur ein Name,
   * und es wäre nicht ablesbar, welches Risiko bei welcher Firma liegt.
   */
  anschrift?: string | null;
  unternehmensfunktion?: string | null;
  taetigkeit?: string | null;
  brancheClassId?: number | null;
  weitereBranchen?: number[];
}

/** Kante des Organigramm-Graphen (relationType gilt aus Sicht von srcId). */
export interface OrgGraphEdge {
  id: number;
  srcId: number;
  destId: number;
  relationType: string;
  share: string | null;
  function: string | null;
  /**
   * Ids gespeicherter Zeilen, die in dieser Kante aufgegangen sind — dieselbe
   * Beteiligung stand z. B. als Tochterfirma UND als Gesellschafter in den
   * Daten (Backend `anteilsbeziehung.ts`).
   */
  zusammengefasst?: number[];
  /** Die zusammengefassten Zeilen tragen verschiedene Quoten. */
  anteilWiderspruch?: string[];
}

export interface CompanyNetworkGraph {
  nodes: OrgGraphNode[];
  edges: OrgGraphEdge[];
}

/**
 * Firmenverbund als Graph fürs Organigramm-Popup.
 * Backend: GET /contact-relation/company-network-graph/:id.
 */
export const fetchCompanyNetworkGraph = async (
  contactId: number,
): Promise<CompanyNetworkGraph> => {
  const { data } = await apiFetch<CompanyNetworkGraph>(
    `contact-relation/company-network-graph/${contactId}`,
  );
  return data ?? { nodes: [], edges: [] };
};

export interface CompanyNetworkState {
  firmen: CompanyNetworkFirma[];
  /**
   * `true`, solange die Antwort aussteht. Bewusst ein eigenes Flag statt
   * `firmen.length === 0`: der Endpoint liefert ausschließlich FIRMEN
   * (`c."typeId" = 1`), für eine Privatperson also eine leere Liste — und im
   * Fehlerfall ebenfalls. Aus der Länge abgeleitet bliebe der Ladezustand in
   * beiden Fällen für immer stehen.
   */
  loading: boolean;
}

/**
 * Lädt den Firmenverbund einer Firma einmalig (bei `contactId`-Wechsel), inkl.
 * Ladezustand. Liefert eine stabile Liste `{id, name}` für die
 * Verbund-Dropdowns. Bei einer Firma enthält die Antwort immer auch die
 * Ausgangsfirma; ist sie isoliert (kein Verbund), kommt nur sie selbst zurück.
 */
export function useCompanyNetworkState(contactId: number | undefined | null): CompanyNetworkState {
  const [state, setState] = useState<CompanyNetworkState>({ firmen: [], loading: false });
  useEffect(() => {
    let alive = true;
    const id = Number(contactId);
    if (!Number.isFinite(id) || id <= 0) {
      setState({ firmen: [], loading: false });
      return;
    }
    // Beim Kontaktwechsel sofort leeren — sonst zeigen die Verbund-Dropdowns
    // bis zum Eintreffen der Antwort die stale Firmenliste des vorherigen
    // Kontakts, aus der sich ein Fremd-Scope wählen ließe.
    setState({ firmen: [], loading: true });
    fetchCompanyNetwork(id)
      .then((f) => {
        if (alive) setState({ firmen: f, loading: false });
      })
      .catch(() => {
        if (alive) setState({ firmen: [], loading: false });
      });
    return () => {
      alive = false;
    };
  }, [contactId]);
  return state;
}

/** Wie {@link useCompanyNetworkState}, nur die Liste — für Aufrufer ohne Ladeanzeige. */
export function useCompanyNetwork(contactId: number | undefined | null): CompanyNetworkFirma[] {
  return useCompanyNetworkState(contactId).firmen;
}
